"use client";

import { ForwardedRef, forwardRef, InputHTMLAttributes, ReactNode, useCallback, useImperativeHandle, useRef, useState } from "react";

import { cn } from "@/lib/utils";

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  icon?: ReactNode;
  useContentWidth?: boolean;
  floatingPlaceholder?: boolean;
};

function ForwardedInput(
  { className, disabled, useContentWidth, style, floatingPlaceholder, ...props }: InputProps,
  ref: ForwardedRef<HTMLInputElement>
) {
  const [contentWidth, setContentWidth] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  const spanRef = useCallback(
    (node: HTMLSpanElement | null) => {
      if (useContentWidth && node) {
        setContentWidth(node.getBoundingClientRect().width);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [useContentWidth, props.value]
  );

  const numberSpinnerWidth = props.type === "number" ? 18 : 0;

  return (
    <div
      className={cn(
        "group relative flex items-center gap-2 bg-white text-sm cursor-text border border-gray-600 hover:border-primary outline-1 outline-primary focus-within:outline focus-within:border-primary rounded-lg px-3 py-2",
        disabled && "hover:border-gray-600 opacity-75 cursor-not-allowed",
        className
      )}
      onClick={() => inputRef.current?.focus()}
    >
      {useContentWidth && (
        <span ref={spanRef} className="absolute invisible border -z-50">
          {props.value}
        </span>
      )}
      {props.icon}
      <input
        ref={inputRef}
        className="w-full placeholder:text-gray-600 bg-transparent outline-none disabled:cursor-not-allowed"
        style={useContentWidth ? { ...style, width: contentWidth + numberSpinnerWidth, minWidth: 8 } : style}
        disabled={disabled}
        {...props}
      />
      {floatingPlaceholder && (
        <span
          className={cn(
            "absolute -top-3 left-2 bg-white px-1 border border-primary-300 rounded-md text-xs opacity-100 visible transition-opacity duration-300 pointer-events-auto",
            !props.value && "opacity-0 invisible pointer-events-none"
          )}
        >
          {props.placeholder}
        </span>
      )}
    </div>
  );
}

const Input = forwardRef(ForwardedInput);
export default Input;
